import { useState, useEffect } from 'react';
import { db, storage } from '../../config/FirebaseConfig';
import { collection, addDoc, deleteDoc, doc, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { FiPlus, FiTrash2, FiX, FiUpload } from 'react-icons/fi';

const AdminGallery = () => {
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    caption: '',
    alt: '',
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      setLoading(true);
      setError(null);
      const galleryCollection = collection(db, 'gallery');
      const snapshot = await getDocs(galleryCollection);
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setGallery(data);
    } catch (error) {
      console.error('Error al obtener galería:', error);
      setError(`Error al cargar galería: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    console.log('Archivo seleccionado para galería:', file.name, file.type, file.size);

    if (!file.type.startsWith('image/')) {
      alert('Por favor selecciona una imagen válida');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('La imagen no debe superar 5MB');
      return;
    }

    setSelectedFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      setError('Por favor selecciona una imagen');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      console.log('Iniciando carga de imagen...');
      
      // Upload file to Storage
      const timestamp = Date.now();
      const storageRef = ref(storage, `gallery/${timestamp}-${selectedFile.name}`);
      console.log('Subiendo archivo a Storage:', storageRef.fullPath);
      await uploadBytes(storageRef, selectedFile);
      console.log('Archivo subido, obteniendo URL...');
      const downloadURL = await getDownloadURL(storageRef);

      // Save to Firestore
      console.log('Guardando documento en Firestore...');
      await addDoc(collection(db, 'gallery'), {
        src: downloadURL,
        caption: formData.caption,
        alt: formData.alt || 'Imagen de galería',
        storagePath: `gallery/${timestamp}-${selectedFile.name}`,
        createdAt: new Date(),
      });

      console.log('Imagen guardada correctamente');
      setError(null);
      alert('Imagen subida correctamente');
      setFormData({ caption: '', alt: '' });
      setSelectedFile(null);
      setPreview(null);
      setShowForm(false);
      fetchGallery();
    } catch (error) {
      console.error('Error completo:', error);
      const errorMessage = error.message || 'Error desconocido';
      setError(`Error al subir la imagen: ${errorMessage}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id, storagePath) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta imagen?')) return;

    try {
      setLoading(true);

      // Delete from Storage
      if (storagePath) {
        try {
          const fileRef = ref(storage, storagePath);
          await deleteObject(fileRef);
        } catch (storageError) {
          console.error('Error al eliminar del storage:', storageError);
        }
      }

      // Delete from Firestore
      await deleteDoc(doc(db, 'gallery', id));
      alert('Imagen eliminada correctamente');
      fetchGallery();
    } catch (error) {
      console.error('Error:', error);
      alert('Error al eliminar la imagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800 dark:text-red-200">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-400 hover:text-red-600"
            >
              <FiX size={20} />
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Gestión de Galería</h2>
          <p className="text-slate-600 dark:text-slate-400 text-sm mt-1">
            Administra las imágenes del portafolio
          </p>
        </div>
        <button
          onClick={() => {
            console.log('Botón Subir Imagen presionado (Galería)');
            setShowForm(!showForm);
            if (showForm) {
              setFormData({ caption: '', alt: '' });
              setSelectedFile(null);
              setPreview(null);
            }
          }}
          className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-semibold transition-colors"
        >
          <FiPlus size={20} />
          Subir Imagen
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Subir Nueva Imagen
            </h3>
            <button
              onClick={() => {
                setShowForm(false);
                setFormData({ caption: '', alt: '' });
                setSelectedFile(null);
                setPreview(null);
              }}
              className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
            >
              <FiX size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* File Upload */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Imagen
              </label>
              <div
                className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-8 text-center hover:border-purple-500 transition-colors cursor-pointer"
                onClick={() => document.getElementById('fileInput')?.click()}
              >
                {preview ? (
                  <div className="space-y-4">
                    <img
                      src={preview}
                      alt="preview"
                      className="max-h-48 mx-auto rounded-lg"
                    />
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {selectedFile?.name}
                    </p>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFile(null);
                        setPreview(null);
                      }}
                      className="text-sm text-red-600 dark:text-red-400 hover:underline"
                    >
                      Cambiar imagen
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <FiUpload size={32} className="mx-auto opacity-50" />
                    <p className="text-slate-900 dark:text-white font-semibold">
                      Haz clic o arrastra una imagen aquí
                    </p>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      PNG, JPG, GIF (máx. 5MB)
                    </p>
                  </div>
                )}
                <input
                  id="fileInput"
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Título/Caption
              </label>
              <input
                type="text"
                value={formData.caption}
                onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
                placeholder="Título de la imagen"
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Texto Alternativo (Alt)
              </label>
              <input
                type="text"
                value={formData.alt}
                onChange={(e) => setFormData({ ...formData, alt: e.target.value })}
                placeholder="Descripción para accesibilidad"
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setFormData({ caption: '', alt: '' });
                  setSelectedFile(null);
                  setPreview(null);
                }}
                className="px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                onClick={() => console.log('Enviar formulario Galería presionado')}
                disabled={uploading || !selectedFile}
                className="px-4 py-2 bg-purple-500 hover:bg-purple-600 disabled:bg-slate-400 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
              >
                {uploading ? 'Subiendo...' : 'Subir Imagen'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Gallery Grid */}
      {loading ? (
        <div className="text-center py-8 text-slate-600 dark:text-slate-400">
          Cargando galería...
        </div>
      ) : gallery.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 dark:bg-slate-800 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700">
          <p className="text-slate-600 dark:text-slate-400 mb-4">No hay imágenes en la galería</p>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-semibold transition-colors"
          >
            <FiPlus size={20} />
            Subir primera imagen
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {gallery.map((item) => (
            <div
              key={item.id}
              className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
            >
              <div className="relative pb-[100%] bg-slate-100 dark:bg-slate-700">
                <img
                  src={item.src}
                  alt={item.alt || 'Imagen'}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <button
                  onClick={() => handleDelete(item.id, item.storagePath)}
                  disabled={loading}
                  className="absolute top-2 right-2 p-2 bg-red-500 hover:bg-red-600 text-white rounded transition-colors disabled:opacity-50"
                >
                  <FiTrash2 size={18} />
                </button>
              </div>
              <div className="p-3">
                {item.caption && (
                  <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                    {item.caption}
                  </p>
                )}
                {item.alt && (
                  <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-2">
                    {item.alt}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminGallery;
